import { useFontPreference } from "@/hooks/useFontPreference";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

/**
 * Compact Sans/Serif toggle for long-form reading surfaces (Feed Reader,
 * Readwise Highlights, etc.). Persists the choice as a per-workspace
 * override so the selection survives reloads and only affects the current
 * workspace.
 */
export const ReadingFontToggle = () => {
  const { effective, setWorkspaceOverride, isSavingWorkspace } = useFontPreference();

  const choose = async (next: "sans" | "serif") => {
    if (next === effective) return;
    try {
      await setWorkspaceOverride(next);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          role="group"
          aria-label="Reading font"
          className="inline-flex items-center rounded-md border border-border bg-background p-0.5"
        >
          <Button
            type="button"
            variant={effective === "sans" ? "secondary" : "ghost"}
            size="sm"
            disabled={isSavingWorkspace}
            onClick={() => choose("sans")}
            className="h-7 px-2"
            aria-pressed={effective === "sans"}
          >
            <span style={{ fontFamily: "'Open Sans', Inter, ui-sans-serif, system-ui, sans-serif" }}>Aa</span>
          </Button>
          <Button
            type="button"
            variant={effective === "serif" ? "secondary" : "ghost"}
            size="sm"
            disabled={isSavingWorkspace}
            onClick={() => choose("serif")}
            className="h-7 px-2"
            aria-pressed={effective === "serif"}
          >
            <span style={{ fontFamily: "Fraunces, ui-serif, Georgia, serif" }}>Aa</span>
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        Reading font for this workspace. Change the account default in Settings.
      </TooltipContent>
    </Tooltip>
  );
};
